import {observer} from 'mobx-react-lite'
import React, {useCallback} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId, Hub, noBoardContainerId, Subtask} from 'yougile-model'
import {Sync} from '@client/sync'
import {LinkButtonNew} from '@components/common/base/buttons'
import {Sortable} from '@components/common/sortable/sortable'
import {NewTaskEditorInput} from '@components/subtasks-old/add-subtask'
import {SubtasksAggregatedInfo} from '@components/subtasks-old/subtasks-aggregated-info'
import {SubtasksStore} from '@components/subtasks-old/subtasks-store'
import {TaskCard} from '@components/task-card'
import {noPropagate, useMemoOne, useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {canShowTask} from '@extensions/mirrored-tasks/permissions'
import LineIcon from './line-l.svg'
import {StoreContext, useStore} from './subtasks-store'

// подзадачи в карточке задачи
const SubtaskListSortable = observer(
  ({
    isParentTaskTemplate,
    boardId
  }: {
    isParentTaskTemplate?: boolean
    boardId?: DataObjectId
  }) => {
    const [state, store] = useStore()

    const box = Sync.getCurrentSandbox()
    const task = box.getHub(store.taskId)

    const isSubtask = task?.location === noBoardContainerId
    if (!boardId && !isSubtask) {
      const col = task && box.getHub(task.location)
      boardId = col?.location
    }

    const taskCard = (task: Hub, placeholder: Subtask | undefined) => (
      <TaskCard
        asSubtask
        remove={
          store.canEdit ? () => store.removeSubtasks([task.id]) : undefined
        }
        task={task}
        withLocationTooltip={
          !state.dragState && task.location !== noBoardContainerId
        }
        onAfterTitleUpdate={title => store.onAfterTitleUpdate(task.id, title)}
        onBeforeOpenChat={store.onBeforeOpenChat}
        isParentTaskTemplate={isParentTaskTemplate}
        boardId={boardId}
        placeholder={placeholder}
        parentTaskId={task.id}
      />
    )

    const subHubs = store.subtasksAsHub.map(hub => {
      const res = canShowTask(hub)
      let placeholder: Subtask | undefined = undefined

      if (!res) {
        const subtasks = Object.values(
          store.task?.data?.subtaskList?.subtasks || {}
        )
        placeholder = subtasks.find(s => s.id === hub.id)
      }

      return [hub.id, taskCard(hub, placeholder)]
    })

    const components = Object.fromEntries(subHubs)

    return (
      <div>
        <Sortable
          onDragChange={store.setDragState}
          components={components}
          onChange={store.setOrder}
          disable={!store.canEdit}
          curvedLines
          lastSubtask={store.subtasks[store.subtasks.length - 1]}
        />
      </div>
    )
  }
)

export const SyncState = (): null => {
  const [, store] = useStore()
  useSyncEvent('current', {debounceTime: 100, filter: '*'}, store.syncState)
  return null
}

interface Props {
  task: Hub
  className?: string
  initialOpen: boolean
  onEndAdd?: () => void
  toggleSubtasksRef?: React.MutableRefObject<(() => void) | undefined>
  isParentTaskTemplate?: boolean
  boardId?: DataObjectId
}

// !подзадачи в карточке задачи
export const TaskSubtasks = (props: Props) => {
  const subtasks = props.task.data?.subtaskList?.subtasks
  const hasSubtasks = subtasks && Object.values(subtasks).length
  if (!props.initialOpen && !hasSubtasks) {
    return null
  }

  return <TaskSubtasksBase {...props} />
}

const TaskSubtasksBase = (props: Props) => {
  const store = useMemoOne(() => {
    const store = new SubtasksStore(props.task.id)
    store.state.showNewTaskInput = props.initialOpen && !store.subtasks.length
    if (props.toggleSubtasksRef) {
      props.toggleSubtasksRef.current = () => {
        store.toggleOpen(true)
        store.setShowNewTaskInput(true)
      }
    }
    return store
  }, [props.task.id, props.toggleSubtasksRef])

  return (
    <StoreContext.Provider value={store}>
      <TaskSubtasksList onEndAdd={props.onEndAdd} {...props} />
      <SyncState />
    </StoreContext.Provider>
  )
}

const TwSubtasksContainer = tw.div<{$open: boolean}>`
  flex
  flex-col
  w-full
  min-w-0
  pb-8
  px-8
  rounded-b-8
  ${p => (p.$open ? 'bg-background-subtask-open' : 'bg-background-disabled')}
`

const TaskSubtasksList = observer(
  (props: {
    onEndAdd?: () => void
    initialOpen?: boolean
    isParentTaskTemplate?: boolean
    boardId?: DataObjectId
  }) => {
    const [state, store] = useStore()

    const empty = !store.subtasks.length
    if (empty && !props.initialOpen) {
      return null
    }

    return (
      <TwSubtasksContainer $open={state.open}>
        {!empty && <SubtasksAggregatedInfo />}
        {state.open && !empty && (
          <SubtaskListSortable
            isParentTaskTemplate={props.isParentTaskTemplate}
            boardId={props.boardId}
          />
        )}
        {state.open && store.canEdit && (
          <CreateSubtask empty={empty} onEndAdd={props.onEndAdd} />
        )}
      </TwSubtasksContainer>
    )
  }
)

export const CreateSubtask = observer(
  (props: {empty: boolean; onEndAdd?: () => void}) => {
    const [state, store] = useStore()

    const onClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        store.setShowNewTaskInput(true)
      },
      [store]
    )

    return (
      <div className={cn('flex', props.empty && 'pt-4')} onClick={noPropagate}>
        <LineIcon className="-ml-8 -mt-4 text-action-default" />
        {state.showNewTaskInput ? (
          <NewTaskEditorInput stayOpen onEndAdd={props.onEndAdd} />
        ) : (
          <LinkButtonNew
            icon="IconPlus"
            color="primary"
            className="h-24"
            onClick={onClick}
            title={Lang.msg.subtask.createSubtask}
          />
        )}
      </div>
    )
  }
)
